import React from 'react';
import { ExclamationTriangleFill, PlugFill, BoxArrowUpRight } from 'react-bootstrap-icons';
import { useLocale } from '../i18n/LocaleContext';
import { describeConnection, formatLastOk } from './shopifyConnection';
import './ShopifyConnectionBanner.scss';

// documents#637 — the "reconnect your store" strip.
//
// Rendered by the Dashboard above every brand page (portal and embedded alike)
// while the selected brand's Shopify link is not CONNECTED:
//
//   REAUTH_REQUIRED  the token was rejected / expired / a scope is missing. The
//                    fix is always the same: open the Outfino app inside the
//                    Shopify admin (reauth_url) — a managed install runs a
//                    fresh token exchange on open.
//   DISCONNECTED     the app is not installed in the store (uninstalled, or the
//                    brand never finished connecting). Same link when the API
//                    has one; otherwise the portal's own connect flow
//                    (`fallbackHref`, the "Add a brand" path) — the embedded
//                    app has no Outfino token for that, so it passes none.
//
// `connection` is the stores[].shopifyConnection block from /user/me (or the
// embedded session's stores[0]); CONNECTED renders nothing.
export default function ShopifyConnectionBanner({ connection, fallbackHref = null }) {
  const { t } = useLocale();
  const view = describeConnection(connection);
  if (!view.show) return null;

  const disconnected = view.status === 'DISCONNECTED';
  const href = view.reauthUrl || (disconnected ? fallbackHref : null);
  // The Shopify admin is a different site: a new tab, so the portal (and the
  // page the merchant was on) stays put. The portal's own connect flow, on
  // the other hand, comes back to this tab with a token — keep it in-tab.
  const newTab = !!view.reauthUrl;
  const lastOk = formatLastOk(view.lastOkAt);
  const Icon = disconnected ? PlugFill : ExclamationTriangleFill;

  return (
    <div
      className={`conn-banner conn-banner--${view.variant}`}
      role="alert"
      data-status={view.status}
    >
      <span className="conn-banner__icon" aria-hidden="true">
        <Icon size={16} />
      </span>

      <div className="conn-banner__text">
        <div className="conn-banner__title">{t(view.titleKey)}</div>
        <div className="conn-banner__meta">
          {view.lastError && (
            <span className="conn-banner__reason">{t(view.lastError.key, view.lastError.vars)}</span>
          )}
          <span className="conn-banner__last-ok">
            {lastOk ? t('connection.lastOk', { time: lastOk }) : t('connection.lastOkNever')}
          </span>
        </div>
      </div>

      {href && (
        <a
          className="conn-banner__cta"
          href={href}
          {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          <span>{t(view.ctaKey)}</span>
          {newTab && <BoxArrowUpRight size={12} />}
        </a>
      )}
    </div>
  );
}
